// Sends moves to the server in order. Moves wait in a queue (kept in localStorage) while the
// server is out of reach and go out on reconnect. If another device moved meanwhile (409),
// the queue is replayed on top of the server's log.
import type { InputMode, Move, MovesRequest, SolveDto } from '@zinc/shared';
import { ApiError, api } from './api';

export interface SyncSnapshot {
  state: unknown;
  elapsedMs: number;
  mistakes: number;
  nudges: number;
  mode: InputMode;
}

export interface SyncDeps {
  post: (solveId: string, body: MovesRequest) => Promise<{ moveCount: number }>;
  reload: (puzzleId: string) => Promise<SolveDto>;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

export const defaultSyncDeps: SyncDeps = {
  post: (solveId, body) =>
    api<{ moveCount: number }>(`/api/solves/${solveId}/moves`, { method: 'POST', json: body }),
  reload: async (puzzleId) =>
    (await api<{ solve: SolveDto }>(`/api/puzzles/${puzzleId}/solve`, { method: 'POST' })).solve,
  storage: typeof localStorage === 'undefined' ? null : localStorage,
};

function safeStorage(s: SyncDeps['storage']) {
  return {
    get(key: string): string | null {
      try {
        return s?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    set(key: string, value: string) {
      try {
        s?.setItem(key, value);
      } catch {
        // Storage full or blocked: the in-memory queue still works.
      }
    },
    remove(key: string) {
      try {
        s?.removeItem(key);
      } catch {
        // ignore
      }
    },
  };
}

/** Moves an earlier visit couldn't send, for a solve. */
export function storedMoves(solveId: string, storage = defaultSyncDeps.storage): Move[] {
  const raw = safeStorage(storage).get(`zincsudoku:queue:${solveId}`);
  if (!raw) return [];
  try {
    const saved = JSON.parse(raw) as { moves: Move[] };
    return Array.isArray(saved.moves) ? saved.moves : [];
  } catch {
    return [];
  }
}

export class MoveSync {
  private queue: Move[] = [];
  private sending = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private retryMs = 1000;
  private readonly store;
  online = true;
  onStatus: (online: boolean, queued: number) => void = () => {};
  /** Called after a 409 with the server's moves plus ours replayed on top. */
  onRebased: (moves: Move[]) => void = () => {};

  constructor(
    private solveId: string,
    private readonly puzzleId: string,
    private serverCount: number,
    private snapshot: () => SyncSnapshot,
    private readonly deps: SyncDeps = defaultSyncDeps,
  ) {
    this.store = safeStorage(deps.storage);
  }

  private key() {
    return `zincsudoku:queue:${this.solveId}`;
  }

  /** Moves left unsent by an earlier visit (closed while offline). */
  takeStored(): Move[] {
    return storedMoves(this.solveId, this.deps.storage);
  }

  push(move: Move) {
    this.queue.push(move);
    this.persist();
    this.schedule(300);
  }

  /** Re-queue moves restored from storage, without persisting twice. */
  restore(moves: Move[]) {
    this.queue.push(...moves);
    this.persist();
    this.schedule(0);
  }

  queued(): number {
    return this.queue.length;
  }

  /** Send now (pause, leaving the page). */
  flush(): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    return this.send();
  }

  /** Timer or counters changed without a move: still worth saving. */
  touch() {
    this.schedule(300);
  }

  dispose() {
    if (this.timer) clearTimeout(this.timer);
  }

  private persist() {
    if (this.queue.length === 0) this.store.remove(this.key());
    else this.store.set(this.key(), JSON.stringify({ moves: this.queue }));
  }

  private schedule(ms: number) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.send();
    }, ms);
  }

  private setOnline(online: boolean) {
    this.online = online;
    this.onStatus(online, this.queue.length);
  }

  private async send(): Promise<void> {
    if (this.sending) return;
    this.sending = true;
    const batch = this.queue.slice(0, 500);
    try {
      const res = await this.deps.post(this.solveId, {
        baseCount: this.serverCount,
        moves: batch,
        ...this.snapshot(),
      });
      this.serverCount = res.moveCount;
      this.queue.splice(0, batch.length);
      this.persist();
      this.retryMs = 1000;
      this.setOnline(true);
      if (this.queue.length > 0) this.schedule(0);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Another device moved: take the server's log and put ours on top.
        const solve = await this.deps.reload(this.puzzleId).catch(() => null);
        if (solve) {
          this.solveId = solve.id;
          this.serverCount = solve.moves.length;
          this.onRebased([...solve.moves, ...this.queue]);
          this.schedule(0);
        }
      } else if (err instanceof ApiError && err.status < 500 && err.status !== 401) {
        // The server refuses these moves for good; drop them rather than loop.
        this.queue.splice(0, batch.length);
        this.persist();
      } else {
        this.setOnline(false);
        this.retryMs = Math.min(this.retryMs * 2, 30_000);
        this.schedule(this.retryMs);
      }
    } finally {
      this.sending = false;
    }
  }
}
